import { useMemo } from 'react';

/**
 * The festival decoration that sits over the portal.
 *
 * Colours come from the theme; this is the layer on top, so turning Diwali on
 * in the admin panel shows up on a page somebody already has open, without a
 * release. It is fixed, full screen and ignores the pointer, so it can never
 * get between somebody and a button.
 *
 * Each festival moves differently - lamps rise, snow falls, confetti tumbles -
 * because that is what makes it read as that festival rather than as generic
 * sparkle. The motion is pure CSS, so it costs nothing to run.
 */

const EFFECTS = {
  diyas: { count: 12, rise: true, spin: false, size: [18, 30], duration: [7, 12] },
  snow: { count: 30, rise: false, spin: false, size: [10, 20], duration: [8, 15] },
  confetti: { count: 26, rise: false, spin: true, size: [12, 20], duration: [4, 8] },
  embers: { count: 16, rise: true, spin: false, size: [12, 22], duration: [5, 10] },
  colors: { count: 18, rise: true, spin: true, size: [20, 34], duration: [6, 11] },
};

const between = ([a, b]) => a + Math.random() * (b - a);

const FestiveOverlay = ({ festival, enabled = true }) => {
  const spec = festival && EFFECTS[festival.effect];

  const pieces = useMemo(() => {
    if (!spec) return [];
    const emojis = festival.emojis && festival.emojis.length ? festival.emojis : ['✨'];
    return Array.from({ length: spec.count }, (_, i) => ({
      key: i,
      emoji: emojis[i % emojis.length],
      left: Math.random() * 100,
      size: between(spec.size),
      duration: between(spec.duration),
      delay: -Math.random() * 12,          // negative: already mid-flight on load
      drift: `${(Math.random() - 0.5) * 140}px`,
    }));
  }, [spec, festival]);

  if (!festival || !enabled || !spec) return null;

  return (
    <div className="festive" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.key}
          className={`festive-bit ${spec.rise ? 'rise' : 'fall'} ${spec.spin ? 'spin' : ''}`}
          style={{
            left: `${p.left}%`,
            fontSize: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            '--drift': p.drift,
            '--glow': festival.glow,
          }}
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
};

export default FestiveOverlay;
